/**
 * Safe localStorage wrapper that handles Safari Private Browsing mode
 * where localStorage may be unavailable or throw errors.
 * Falls back to in-memory storage when localStorage is not available.
 * 
 * The in-memory fallback uses an LRU (Least Recently Used) eviction policy
 * to prevent unbounded memory growth. Default limit is 100 items.
 */
class SafeStorage {
  private memoryStorage: Map<string, string> = new Map();
  private isLocalStorageAvailable: boolean;
  private readonly maxMemoryItems: number;

  /**
   * @param maxMemoryItems Maximum number of items to store in memory fallback (default: 100)
   */
  constructor(maxMemoryItems: number = 100) {
    this.maxMemoryItems = maxMemoryItems;
    this.isLocalStorageAvailable = this.checkLocalStorage();
  }

  /**
   * Check if localStorage is available and working
   */
  private checkLocalStorage(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      // localStorage is not available (Safari Private Browsing, etc.)
      console.warn('localStorage is not available, using in-memory fallback');
      return false;
    }
  }

  /**
   * Move a key to the most recent position (LRU update)
   * Map maintains insertion order, so delete and re-insert moves to end
   */
  private touchKey(key: string, value: string): void {
    this.memoryStorage.delete(key);
    this.memoryStorage.set(key, value);
  }

  /**
   * Evict the oldest entry if we're at capacity
   */
  private evictIfNeeded(): void {
    if (this.memoryStorage.size >= this.maxMemoryItems) {
      // Get the first (oldest) key and remove it
      const oldestKey = this.memoryStorage.keys().next().value;
      if (oldestKey !== undefined) {
        this.memoryStorage.delete(oldestKey);
      }
    }
  }

  /**
   * Get item from storage
   */
  getItem(key: string): string | null {
    if (this.isLocalStorageAvailable) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        // Fallback if localStorage throws during operation
        const value = this.memoryStorage.get(key);
        if (value !== undefined) {
          // Update LRU order on access
          this.touchKey(key, value);
        }
        return value !== undefined ? value : null;
      }
    }

    const value = this.memoryStorage.get(key);
    if (value !== undefined) {
      // Update LRU order on access
      this.touchKey(key, value);
    }
    return value !== undefined ? value : null;
  }

  /**
   * Set item in storage
   * For in-memory storage, uses LRU eviction when at capacity.
   * 
   * Consistency guarantee: localStorage is written first when available.
   * memoryStorage is only updated if localStorage write succeeds (or is unavailable).
   * This ensures getItem and setItem remain consistent.
   */
  setItem(key: string, value: string): void {
    if (this.isLocalStorageAvailable) {
      // localStorage is available - try to write there first
      try {
        localStorage.setItem(key, value);
        // localStorage write succeeded, now update memoryStorage for consistency
        if (!this.memoryStorage.has(key)) {
          this.evictIfNeeded();
        }
        this.touchKey(key, value);
      } catch (e) {
        // localStorage write failed (quota exceeded, etc.)
        // Do NOT update memoryStorage to maintain consistency with localStorage
        // (getItem will return the old localStorage value, not this new value)
        console.warn('Failed to write to localStorage:', key, e);
      }
    } else {
      // localStorage is not available - use memory storage only
      if (!this.memoryStorage.has(key)) {
        this.evictIfNeeded();
      }
      this.touchKey(key, value);
    }
  }

  /**
   * Remove item from storage
   */
  removeItem(key: string): void {
    if (this.isLocalStorageAvailable) {
      try {
        localStorage.removeItem(key);
        // Only remove from memory if localStorage removal succeeded
        this.memoryStorage.delete(key);
      } catch (e) {
        // If localStorage fails, keep memory consistent with it (do nothing)
        console.warn('Failed to remove from localStorage:', key, e);
      }
    } else {
      // If no localStorage, just update memory
      this.memoryStorage.delete(key);
    }
  }

  /**
   * Clear all storage
   */
  clear(): void {
    if (this.isLocalStorageAvailable) {
      try {
        localStorage.clear();
        // Only clear memory if localStorage clear succeeded
        this.memoryStorage.clear();
      } catch (e) {
        // If localStorage fails, keep memory consistent
        console.warn('Failed to clear localStorage:', e);
      }
    } else {
      // If no localStorage, just clear memory
      this.memoryStorage.clear();
    }
  }

  /**
   * Check if storage is using localStorage or memory fallback
   */
  isUsingLocalStorage(): boolean {
    return this.isLocalStorageAvailable;
  }

  /**
   * Get the current size of in-memory storage (for debugging)
   */
  getMemoryStorageSize(): number {
    return this.memoryStorage.size;
  }
}

// Export singleton instance with default limit of 100 items
export const safeStorage = new SafeStorage(100);

// Export class for testing purposes or custom instances
export { SafeStorage };
